// src/pages/Brand/components/BrandTable.js
import React from 'react';
import { Star, ChevronDown, } from 'lucide-react';
// import OrbitLoader from '../../../components/Loader/OrbitLoader.js';
// import CircleLoader from '../../../components/Loader/CircleLoader.js';
import './BrandSearch.css';
import { encodeBrandId } from '../../../utils/urlUtils';
import { useLanguage } from '../../../contexts/LanguageContext';

const LoadingSkeleton = () => {
  const { translate } = useLanguage();

  return (
    <div className="table-container" style={{
      minHeight: "500px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      textAlign: 'center',
      gap: '25px'
    }}>
      <div className="loading-shimmer" />
      <div className="loading-content">
        <div className="loading-spinner" />
        <span className="loading-text">{translate('brandPage.loading.dataLoading')}</span>
      </div>
      <div className="table-skeleton">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="skeleton-row">
            <div className="skeleton-cell rank" />
            <div className="skeleton-cell brand" />
            <div className="skeleton-cell rating" />
            <div className="skeleton-cell reviews" />
            <div className="skeleton-cell trend" />
            <div className="skeleton-cell health" />
            <div className="skeleton-cell category" />
          </div>
        ))}
      </div>
    </div>
  );
};

const StarGradientDef = () => (
  // display:none (Tailwind `hidden`) would stop the browser from rendering
  // the gradient defs, breaking every fill that references them — keep the
  // svg in the render tree at zero size instead
  <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true" focusable="false">
    <defs>
      <linearGradient id="star-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" style={{ stopColor: '#fcd34d' }} />
        <stop offset="100%" style={{ stopColor: '#f59e0b' }} />
      </linearGradient>
      <linearGradient id="star-gradient-half" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="50%" style={{ stopColor: '#f59e0b' }} />
        <stop offset="50%" style={{ stopColor: 'transparent' }} />
      </linearGradient>
    </defs>
  </svg>
);

const StarRating = ({ rating }) => {
  const totalStars = 5;
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;

  return (
    <div className="star-group">
      {[...Array(totalStars)].map((_, index) => (
        <Star
          key={index}
          className={`star ${index < fullStars
              ? 'star-filled'
              : index === fullStars && hasHalfStar
                ? 'star-half'
                : 'star-empty'
            }`}
          style={{
            fill: index < fullStars
              ? 'url(#star-gradient)'
              : index === fullStars && hasHalfStar
                ? 'url(#star-gradient-half)'
                : 'none',
            stroke: '#f59e0b',
            strokeWidth: '1px'
          }}
        />
      ))}
    </div>
  );
};

const BrandTable = ({ brands, loading, sortConfig, onSort, currentPage, perPage, isSearchMode }) => {
  if (loading) {
    return <LoadingSkeleton />;
  }

  // const getSortIcon = (key) => {
  //   if (sortConfig.key !== key) {
  //     return <ChevronDown className="h-4 w-4 ml-2 text-slate-400" />;
  //   }
  //   return sortConfig.direction === 'asc' ? (
  //     <ChevronUp className="h-4 w-4 ml-2 text-blue-500" />
  //   ) : (
  //     <ChevronDown className="h-4 w-4 ml-2 text-blue-500" />
  //   );
  // };

  const getSortableHeaderClass = (key) => {
    return `sortable ${key}-cell ${sortConfig.key === key ? 'active' : ''}`;
  };

  const getHealthBadgeClass = (health) => {
    switch (health) {
      case 'Strong':
        return 'health-badge strong';
      case 'Stable':
        return 'health-badge stable';
      default:
        return 'health-badge weak';
    }
  };

  const getRankNumber = (index, brand) => {
    if (isSearchMode) {
      return String(brand.ranking.rank).padStart(2, '0');
    }
    const rankNumber = ((currentPage - 1) * perPage) + index + 1;
    return String(rankNumber).padStart(2, '0');
  };

  const isFirstPage = currentPage === 1;

  return (
    <div className="table-container">
      <StarGradientDef />
      <table>
        <thead>
          <tr>
            <th className="rank-cell">Rank</th>
            <th className="brand-cell">Brand</th>
            {/* <th 
              className={getSortableHeaderClass('weighted_rating')}
              onClick={() => onSort('weighted_rating')}
            >
              <div className="header-content">
                <span>Rating</span>
                <ChevronDown className={`sort-icon ${
                  sortConfig.key === 'weighted_rating' ? 'text-blue-500' : 'text-slate-400'
                }`} />
              </div>
            </th> */}

            <th
              className={getSortableHeaderClass('search_trend')}
              onClick={() => onSort('search_trend')}
            >
              <div className="header-content">
                <span>Trend</span>
                <ChevronDown className={`sort-icon ${sortConfig.key === 'search_trend' ? 'text-blue-500' : 'text-slate-400'
                  }`} />
              </div>
            </th>
            <th
              className={getSortableHeaderClass('total_reviews')}
              onClick={() => onSort('total_reviews')}
            >
              <div className="header-content">
                <span>Reviews</span>
                <ChevronDown className={`sort-icon ${sortConfig.key === 'total_reviews' ? 'text-blue-500' : 'text-slate-400'
                  }`} />
              </div>
            </th>
            {/* <th 
              className={getSortableHeaderClass('search_trend')}
              onClick={() => onSort('search_trend')}
            >
              <div className="header-content">
                <span>Trend</span>
                <ChevronDown className={`sort-icon ${
                  sortConfig.key === 'search_trend' ? 'text-blue-500' : 'text-slate-400'
                }`} />
              </div>
            </th> */}


            <th
              className={getSortableHeaderClass('weighted_rating')}
              onClick={() => onSort('weighted_rating')}
            >
              <div className="header-content">
                <span>Rating</span>
                <ChevronDown className={`sort-icon ${sortConfig.key === 'weighted_rating' ? 'text-blue-500' : 'text-slate-400'
                  }`} />
              </div>
            </th>

            <th className="health-cell">Health</th>
            <th className="category-cell">Category</th>
          </tr>
        </thead>
        <tbody>
          {brands.map((brand, index) => (
            <tr key={brand.brand_id}>
              <td className="rank-cell">
                <span className={`rank-number ${isFirstPage && index < 3 ? 'top-3' : 'normal'}`}>
                  {getRankNumber(index, brand)}
                </span>
              </td>
              <td className="brand-cell">
                <a
                  href={`/brand/${encodeBrandId(brand.brand_id)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="brand-link"
                >
                  <span
                    className="font-medium custom-brand-font"
                  >
                    {brand.brand_name}
                  </span>
                </a>
              </td>
              {/* <td className="rating-cell">
                <div className="star-rating-container">
                  <StarRating rating={brand.weighted_rating} />
                  <span className="rating-text">
                    {brand.weighted_rating.toFixed(1)}
                  </span>
                </div>
              </td> */}
              <td className="trend-cell">
                {brand.search_trend.toLocaleString()}
              </td>
              <td className="reviews-cell">
                {brand.total_reviews.toLocaleString()}
              </td>
              <td className="rating-cell">
                <div className="star-rating-container">
                  <StarRating rating={brand.weighted_rating} />
                  <span className="rating-text">
                    {brand.weighted_rating.toFixed(1)}
                  </span>
                </div>
              </td>
              {/* <td className="trend-cell">
                {brand.search_trend.toLocaleString()}
              </td> */}
              <td className="health-cell">
                <span className={getHealthBadgeClass(brand.brand_health_score)}>
                  {brand.brand_health_score === 'Needs Attention' ? 'Attention' : brand.brand_health_score}
                </span>
              </td>
              <td className="category-cell">
                <div className="categories-list">
                  {brand.categories.map((category, i) => (
                    <span key={i} className="category-tag">
                      {category}
                    </span>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default BrandTable;
